/**
 * TreeDashboardPage Component
 *
 * Main dashboard page for the family tree visualization.
 * Composes all dashboard components into a cohesive experience.
 *
 * Layout:
 * - Top: Navigation bar
 * - Left: Tool rail for view modes
 * - Center: Tree canvas with person nodes
 * - Right: Details panel for selected person
 * - Bottom: Floating control bar
 *
 * State Management:
 * - Tree data from useTreeCanvas hook (real Supabase data)
 * - Selected person state
 * - Visualization mode state
 * - Canvas transform (pan/zoom) state
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TopNavigation } from '../components/TopNavigation';
import { LeftToolRail } from '../components/LeftToolRail';
import { FamilyTreeCanvas } from '../components/FamilyTreeCanvas';
import { RightDetailsPanel } from '../components/RightDetailsPanel';
import { BottomControlBar } from '../components/BottomControlBar';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { AddPersonModal, type PersonFormData, type AvailablePartner, type PartnerSelection, type CoupleMetadata } from '../components/AddPersonModal';
import { RelationshipChooserModal, type RelationshipType } from '../components/RelationshipChooserModal';
import { CreateTreeModal } from '../components/CreateTreeModal';
import { useTreeCanvas } from '../hooks/useTreeCanvas';
import { useCanvasTransform } from '../hooks/useCanvasTransform';
import { createPerson, createRelationship, createCouple, createTreeWithSelfPerson } from '@/api/tree-dashboard.api';
import { uploadPersonImage } from '@/api/storage.api';
import type { TreeVisualizationMode } from '../types';
import {
  validateMutation,
  buildGraphContext,
  getSpouses,
  getParents,
  type AddParentMutation,
  type AddChildMutation,
  type AddSpouseMutation,
  type AddSiblingMutation,
} from '../validation';
import { relationshipDomainService } from '@/services/RelationshipDomainService';

/**
 * Helper function to get translated error message from validation violation
 */
function getValidationErrorMessage(violation: any, personName: string, t: any): string {
  if (violation.code === 'INVALID_PARENT_COUNT') {
    return t('tree.errors.maxParentsReached', { name: personName });
  }
  // Fallback to original message for other error codes
  return violation.message || 'This action would violate family tree rules.';
}

/**
 * Helper: Ensure spouse relationship and couple record exist for two parents
 * Used in Add Child flows to create IMPLIED co-parent connections.
 *
 * CORE PRODUCT RULE:
 * Add Child may create or complete an implied spouse/couple connection for tree structure,
 * but it does NOT force explicit relationship metadata entry.
 *
 * This helper:
 * - Ensures a spouse relationship exists (if missing)
 * - Ensures a couple record exists (if missing)
 * - Uses minimal/optional metadata (no forced dates or marital status)
 * - Treats all errors as non-fatal (defensive - implied connection may already exist)
 *
 * @param treeId - Tree ID
 * @param parent1Id - First parent ID
 * @param parent2Id - Second parent ID
 */
async function ensureImpliedCoupleConnection(
  treeId: string,
  parent1Id: string,
  parent2Id: string
): Promise<void> {
  // Step 1: Ensure spouse relationship exists (defensive - may already exist from Add Spouse flow)
  try {
    await createRelationship(treeId, parent1Id, parent2Id, 'spouse');
    console.log('[ensureImpliedCoupleConnection] Spouse relationship created');
  } catch (error: any) {
    // Non-fatal: spouse relationship might already exist
    const isDuplicateError = error?.code === '23505' || error?.message?.includes('unique_relationship');
    if (!isDuplicateError) {
      console.warn('[ensureImpliedCoupleConnection] Unexpected error creating spouse relationship (non-fatal):', error);
    }
  }

  // Step 2: Ensure couple record exists (defensive - may already exist)
  // IMPORTANT: Use truly minimal metadata - no forced dates or marital status
  // The couple record is for tree visualization structure only, not relationship claims
  try {
    await createCouple(treeId, parent1Id, parent2Id, {
      // Empty metadata object - let API/database defaults handle everything
      // This avoids forcing is_married: false which would be a semantic claim
    });
    console.log('[ensureImpliedCoupleConnection] Couple record created');
  } catch (error: any) {
    // Non-fatal: couple record might already exist
    console.warn('[ensureImpliedCoupleConnection] Failed to create couple record (non-fatal):', error);
  }
}

export function TreeDashboardPage() {
  const { t } = useTranslation('common');
  const [visualizationMode, setVisualizationMode] = useState<TreeVisualizationMode>('vertical');

  // Mobile menu state - MUST be declared before any conditional returns
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDetailsPanelOpen, setIsMobileDetailsPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Add Person Modal state
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [addPersonContext, setAddPersonContext] = useState<{
    type: 'parent' | 'child' | 'spouse' | 'sibling';
    relationshipType: 'father' | 'mother' | 'son' | 'daughter' | 'spouse' | 'brother' | 'sister';
    sourcePersonId: string;
    sourcePersonName: string;
    allowedGenders?: ('male' | 'female')[];
    otherParentId?: string; // For child relationships when there's a clear spouse
    availablePartners?: AvailablePartner[]; // For child partner selection
    parentIds?: string[]; // For sibling relationships - inherited parent IDs
  } | null>(null);

  // Relationship Chooser Modal state
  const [showRelationshipChooser, setShowRelationshipChooser] = useState(false);
  const [relationshipChooserPerson, setRelationshipChooserPerson] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Create Tree Modal state (NO-TREE state)
  const [showCreateTreeModal, setShowCreateTreeModal] = useState(false);

  // Person positions state for search centering
  const [personPositions, setPersonPositions] = useState<Map<string, { x: number; y: number }>>(new Map());

  // Tree data hook
  const {
    canvasData,
    personDetail,
    selectedPersonId,
    isLoadingCanvas,
    isLoadingDetail,
    canvasError,
    selectPerson,
    clearSelection,
    refresh,
  } = useTreeCanvas();

  // Canvas transform hook (pan/zoom)
  const canvasTransform = useCanvasTransform({
    minScale: 0.5,
    maxScale: 2.0,
    initialTransform: { x: 0, y: 0, scale: 1 },
  });

  // Calculate total generations from canvas data
  const totalGenerations = useMemo(() => {
    if (!canvasData || canvasData.persons.length === 0) return 0;

    // Simple generation calculation
    // TODO: Could be enhanced to use actual relationship depth
    return 3;
  }, [canvasData]);

  // Check if mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-open details panel on mobile when person is selected
  useEffect(() => {
    if (selectedPersonId && isMobile) {
      setIsMobileDetailsPanelOpen(true);
    }
  }, [selectedPersonId, isMobile]);

  const handleModeChange = (mode: TreeVisualizationMode) => {
    setVisualizationMode(mode);
    // Show toast for modes not yet implemented
    if (mode !== 'vertical') {
      toast.info(t('tree.toasts.viewComingSoon', { view: mode.charAt(0).toUpperCase() + mode.slice(1) }));
    }
  };

  const handleExport = () => {
    toast.info(t('tree.toasts.exportComingSoon'));
  };

  // Handle directional pan (for mobile/tablet pan controls)
  const handlePan = (direction: 'up' | 'down' | 'left' | 'right') => {
    const PAN_DISTANCE = 100; // pixels to pan
    switch (direction) {
      case 'up':
        canvasTransform.pan(0, PAN_DISTANCE);
        break;
      case 'down':
        canvasTransform.pan(0, -PAN_DISTANCE);
        break;
      case 'left':
        canvasTransform.pan(PAN_DISTANCE, 0);
        break;
      case 'right':
        canvasTransform.pan(-PAN_DISTANCE, 0);
        break;
    }
  };

  // Handle search person selection - centers the tree on the selected person
  const handleSearchPersonSelect = (personId: string) => {
    // Select the person (this will load their details in the right panel)
    selectPerson(personId);

    // Center the tree on the selected person's position
    const position = personPositions.get(personId);
    if (position) {
      // Use viewport dimensions (window size) for centering
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate center of the person node (NODE_WIDTH = 224, NODE_HEIGHT = 80)
      const NODE_WIDTH = 224;
      const NODE_HEIGHT = 80;
      const personCenterX = position.x + NODE_WIDTH / 2;
      const personCenterY = position.y + NODE_HEIGHT / 2;

      canvasTransform.centerOnPoint(personCenterX, personCenterY, viewportWidth, viewportHeight);
      console.log('[TreeDashboardPage] Centered on person:', personId, position);
    } else {
      console.warn('[TreeDashboardPage] Person position not found for:', personId);
    }
  };

  // Handle creating tree from profile (NO-TREE state)
  const handleCreateTreeFromProfile = async () => {
    try {
      toast.info(t('tree.toasts.creatingTree', 'Creating your family tree...'));

      const { treeId, personId } = await createTreeWithSelfPerson();

      console.log('[TreeDashboardPage] Tree created successfully:', { treeId, personId });

      toast.success(t('tree.toasts.treeCreated', 'Your family tree has been created!'));

      // Refresh the tree canvas to load the new tree
      refresh();
    } catch (error) {
      console.error('[TreeDashboardPage] Failed to create tree:', error);
      toast.error(t('tree.errors.createTreeFailed', 'Failed to create tree. Please try again.'));
      throw error; // Re-throw to keep modal open and show loading state
    }
  };

  // Handle adding a parent
  const handleAddParent = (childId: string, parentType: 'father' | 'mother') => {
    const child = canvasData?.persons.find(p => p.id === childId);
    if (!child || !canvasData) return;

    // PRE-VALIDATION: Check if adding this parent is valid BEFORE opening modal
    // This prevents the user from filling out the form only to be blocked later

    // Create a temporary person ID for validation (will be replaced by real ID after creation)
    const tempParentId = 'temp-parent-' + crypto.randomUUID();

    // Build validation context
    const graphContext = buildGraphContext(canvasData);

    // Create mutation for validation
    const mutation: AddParentMutation = {
      type: 'add_parent',
      sourcePersonId: childId, // The child
      targetPersonId: tempParentId, // The new parent (temporary ID)
      parentGender: parentType === 'father' ? 'male' : 'female',
    };

    // Validate the mutation
    const validationResult = validateMutation(mutation, graphContext);

    if (!validationResult.valid) {
      // Block the action - show user-friendly error
      const firstError = validationResult.violations.find(v => v.severity === 'error');
      const childName = `${child.first_name} ${child.last_name || ''}`.trim();
      const errorMessage = firstError ? getValidationErrorMessage(firstError, childName, t) : 'This action would violate family tree rules.';

      toast.error(t('tree.errors.cannotAddParent', { parentType }), {
        description: errorMessage,
        duration: 5000,
      });
      console.warn('[TreeDashboardPage] Add parent blocked by validation:', {
        childId,
        parentType,
        violations: validationResult.violations,
      });
      return;
    }

    // Validation passed - open modal
    const childName = `${child.first_name} ${child.last_name || ''}`.trim();
    setAddPersonContext({
      type: 'parent',
      relationshipType: parentType,
      sourcePersonId: childId,
      sourcePersonName: childName,
    });
    setShowAddPersonModal(true);
  };

  // Handle adding a child (son or daughter)
  const handleAddChild = (parentId: string, childType: 'son' | 'daughter') => {
    const parent = canvasData?.persons.find(p => p.id === parentId);
    if (!parent || !canvasData) return;

    // Build validation context
    const graphContext = buildGraphContext(canvasData);

    // Get spouses to check for automatic second parent
    const spouseIds = getSpouses(graphContext, parentId);

    // Create a temporary child ID for validation
    const tempChildId = 'temp-child-' + crypto.randomUUID();

    // Create mutation for validation
    // Only auto-set otherParentId if there's exactly ONE spouse
    const mutation: AddChildMutation = {
      type: 'add_child',
      sourcePersonId: parentId, // The parent
      targetPersonId: tempChildId, // The new child (temporary ID)
      otherParentId: spouseIds.length === 1 ? spouseIds[0] : undefined,
    };

    // Validate the mutation
    const validationResult = validateMutation(mutation, graphContext);

    if (!validationResult.valid) {
      // Block the action - show user-friendly error
      const firstError = validationResult.violations.find(v => v.severity === 'error');
      const errorMessage = firstError?.message || 'This action would violate family tree rules.';

      toast.error(t('tree.errors.cannotAddChild', { childType }), {
        description: errorMessage,
        duration: 5000,
      });
      console.warn('[TreeDashboardPage] Add child blocked by validation:', {
        parentId,
        childType,
        violations: validationResult.violations,
      });
      return;
    }

    // Build available partners list (only the parent's spouses)
    const availablePartners: AvailablePartner[] = spouseIds
      .map(spouseId => {
        const spouse = canvasData.persons.find(p => p.id === spouseId);
        if (!spouse) return null;
        return {
          id: spouse.id,
          name: `${spouse.first_name} ${spouse.last_name || ''}`.trim(),
        };
      })
      .filter((partner): partner is AvailablePartner => partner !== null);

    // Validation passed - open modal
    const parentName = `${parent.first_name} ${parent.last_name || ''}`.trim();
    setAddPersonContext({
      type: 'child',
      relationshipType: childType,
      sourcePersonId: parentId,
      sourcePersonName: parentName,
      otherParentId: spouseIds.length === 1 ? spouseIds[0] : undefined, // Auto-set only if 1 spouse
      availablePartners,
    });
    setShowAddPersonModal(true);
  };

  // Handle adding a spouse
  const handleAddSpouse = (personId: string) => {
    const person = canvasData?.persons.find(p => p.id === personId);
    if (!person || !canvasData) return;

    // Build validation context
    const graphContext = buildGraphContext(canvasData);

    // Create a temporary spouse ID for validation
    const tempSpouseId = 'temp-spouse-' + crypto.randomUUID();

    // Use domain service to determine allowed genders for spouse
    const allowedGenders = relationshipDomainService.getAllowedSpouseGenders(person.gender);

    // Create mutation for validation
    const mutation: AddSpouseMutation = {
      type: 'add_spouse',
      sourcePersonId: personId,
      targetPersonId: tempSpouseId,
    };

    // Validate the mutation
    const validationResult = validateMutation(mutation, graphContext);

    if (!validationResult.valid) {
      // Block the action - show user-friendly error
      const firstError = validationResult.violations.find(v => v.severity === 'error');
      const errorMessage = firstError?.message || 'This action would violate family tree rules.';

      toast.error(t('tree.errors.cannotAddSpouse'), {
        description: errorMessage,
        duration: 5000,
      });
      console.warn('[TreeDashboardPage] Add spouse blocked by validation:', {
        personId,
        violations: validationResult.violations,
      });
      return;
    }

    // Validation passed - open modal
    const personName = `${person.first_name} ${person.last_name || ''}`.trim();
    setAddPersonContext({
      type: 'spouse',
      relationshipType: 'spouse',
      sourcePersonId: personId,
      sourcePersonName: personName,
      allowedGenders,
    });
    setShowAddPersonModal(true);
  };

  // Handle adding a sibling (brother or sister)
  const handleAddSibling = (personId: string, siblingType: 'brother' | 'sister') => {
    const sourcePerson = canvasData?.persons.find(p => p.id === personId);
    if (!sourcePerson || !canvasData) return;

    // Build validation context
    const graphContext = buildGraphContext(canvasData);

    // Get source person's parents - these will be inherited by the sibling
    const parentIds = getParents(graphContext, personId);

    // PRE-VALIDATION: Check if source person has at least one parent
    if (parentIds.length === 0) {
      const personName = `${sourcePerson.first_name} ${sourcePerson.last_name || ''}`.trim();
      toast.error(t('tree.errors.cannotAddSibling'), {
        description: t('tree.errors.noParentsForSibling', { name: personName }),
        duration: 5000,
      });
      console.warn('[TreeDashboardPage] Add sibling blocked - no parents:', {
        sourcePersonId: personId,
        siblingType,
      });
      return;
    }

    // Create a temporary sibling ID for validation
    const tempSiblingId = 'temp-sibling-' + crypto.randomUUID();

    // Create mutation for validation
    const mutation: AddSiblingMutation = {
      type: 'add_sibling',
      sourcePersonId: personId,
      targetPersonId: tempSiblingId,
      siblingGender: siblingType === 'brother' ? 'male' : 'female',
      parentIds,
    };

    // Validate the mutation
    const validationResult = validateMutation(mutation, graphContext);

    if (!validationResult.valid) {
      // Block the action - show user-friendly error
      const firstError = validationResult.violations.find(v => v.severity === 'error');
      const errorMessage = firstError?.message || 'This action would violate family tree rules.';

      toast.error(t('tree.errors.cannotAddSibling'), {
        description: errorMessage,
        duration: 5000,
      });
      console.warn('[TreeDashboardPage] Add sibling blocked by validation:', {
        personId,
        siblingType,
        violations: validationResult.violations,
      });
      return;
    }

    // Prepare context message for single parent (half-sibling) case
    const personName = `${sourcePerson.first_name} ${sourcePerson.last_name || ''}`.trim();
    let contextMessage = '';
    if (parentIds.length === 1) {
      // Single parent - this will create a half-sibling
      const parent = canvasData.persons.find(p => p.id === parentIds[0]);
      const parentName = parent ? `${parent.first_name} ${parent.last_name || ''}`.trim() : 'one parent';
      contextMessage = t('tree.info.halfSiblingNotice', {
        defaultValue: `This sibling will share only ${parentName} with ${personName}`,
      });
    }

    // Validation passed - open modal
    setAddPersonContext({
      type: 'sibling',
      relationshipType: siblingType,
      sourcePersonId: personId,
      sourcePersonName: personName,
      allowedGenders: [siblingType === 'brother' ? 'male' : 'female'], // Enforce gender
      parentIds, // Pass parent IDs for inheritance
    });

    // Show info message for half-sibling case
    if (contextMessage) {
      toast.info(contextMessage, { duration: 4000 });
    }

    setShowAddPersonModal(true);
  };

  // Handle opening relationship chooser
  const handleAddRelative = (personId: string) => {
    const person = canvasData?.persons.find(p => p.id === personId);
    if (!person) return;

    const personName = `${person.first_name} ${person.last_name || ''}`.trim();
    setRelationshipChooserPerson({ id: personId, name: personName });
    setShowRelationshipChooser(true);
  };

  // Handle relationship type selection from chooser
  const handleRelationshipTypeSelected = (relationshipType: RelationshipType) => {
    setShowRelationshipChooser(false);

    if (!relationshipChooserPerson || !canvasData) {
      setRelationshipChooserPerson(null);
      return;
    }

    const sourcePerson = canvasData.persons.find(p => p.id === relationshipChooserPerson.id);
    if (!sourcePerson) {
      setRelationshipChooserPerson(null);
      return;
    }

    // Handle different relationship types
    if (relationshipType === 'son' || relationshipType === 'daughter') {
      handleAddChild(relationshipChooserPerson.id, relationshipType);
    } else if (relationshipType === 'spouse') {
      handleAddSpouse(relationshipChooserPerson.id);
    } else if (relationshipType === 'father' || relationshipType === 'mother') {
      // These should use the Add Parent buttons, but we can support them here too
      handleAddParent(relationshipChooserPerson.id, relationshipType);
    } else if (relationshipType === 'brother' || relationshipType === 'sister') {
      // Handle sibling creation with shared-parents logic
      handleAddSibling(relationshipChooserPerson.id, relationshipType);
    } else {
      // Unknown relationship type
      toast.error(t('tree.errors.unknownRelationship'), {
        duration: 4000,
      });
    }

    // Reset state
    setRelationshipChooserPerson(null);
  };

  // Handle submitting the add person form
  const handleAddPersonSubmit = async (
    personData: PersonFormData,
    profileImageFile: File | null,
    partnerSelection?: PartnerSelection,
    coupleMetadata?: CoupleMetadata
  ) => {
    if (!canvasData || !addPersonContext) return;

    try {
      // Step 1: Upload image if provided
      let profileImageUrl: string | null = null;
      if (profileImageFile) {
        toast.info(t('tree.toasts.uploadingImage'));
        // Create temporary person ID for image upload
        const tempPersonId = crypto.randomUUID();
        profileImageUrl = await uploadPersonImage(tempPersonId, profileImageFile);
      }

      // Step 2: Create the new person
      toast.info(t('tree.toasts.creatingPerson'));
      const newPersonId = await createPerson(canvasData.tree.id, {
        first_name: personData.first_name,
        last_name: personData.last_name || null,
        nickname: personData.nickname || null,
        gender: personData.gender,
        date_of_birth: personData.date_of_birth || null,
        date_of_death: personData.date_of_death || null,
        marital_status: personData.marital_status,
        birth_place: personData.birth_place || null,
        death_place: personData.death_place || null,
        notes: personData.notes || null,
        profile_image_url: profileImageUrl,
      });

      // Step 3: Create relationships using domain service
      const relationshipPlan = relationshipDomainService.planRelationshipCreation({
        relationshipType: addPersonContext.relationshipType,
        sourcePersonId: addPersonContext.sourcePersonId,
        targetPersonId: newPersonId,
        targetPersonGender: personData.gender,
        treeId: canvasData.tree.id,
        parentIds: addPersonContext.parentIds, // For siblings
      });

      if (!relationshipPlan.success) {
        // This shouldn't happen as we validated beforehand, but handle gracefully
        throw new Error(relationshipPlan.errors?.[0] || 'Failed to plan relationship creation');
      }

      // Create all planned relationships
      for (const rel of relationshipPlan.relationshipsToCreate) {
        await createRelationship(
          canvasData.tree.id,
          rel.personAId,
          rel.personBId,
          rel.relationshipType
        );
      }

      // Handle additional relationships for specific contexts
      if (addPersonContext.type === 'parent') {
        toast.success(t('tree.toasts.parentAdded', {
          parentType: addPersonContext.relationshipType.charAt(0).toUpperCase() + addPersonContext.relationshipType.slice(1)
        }));
      } else if (addPersonContext.type === 'child') {
        // PATH 1: Auto-detected other parent (exactly one spouse exists)
        if (addPersonContext.otherParentId) {
          try {
            await createRelationship(
              canvasData.tree.id,
              addPersonContext.otherParentId, // other parent (person_a)
              newPersonId, // child (person_b)
              'parent_child'
            );
          } catch (error: any) {
            // Defensive: If duplicate relationship error (23505), treat as non-fatal
            // since the intended state already exists
            const isDuplicateError = error?.code === '23505' || error?.message?.includes('unique_relationship');
            if (!isDuplicateError) {
              throw error; // Re-throw non-duplicate errors
            }
            console.warn('[TreeDashboardPage] Duplicate parent_child relationship detected (non-fatal):', error);
          }

          // Ensure implied couple connection exists (spouse + couple record)
          // This uses the unified helper with minimal metadata (no forced is_married value)
          await ensureImpliedCoupleConnection(
            canvasData.tree.id,
            addPersonContext.sourcePersonId, // current parent
            addPersonContext.otherParentId  // auto-detected spouse
          );
        }

        // PATH 2 & 3: Handle manual partner selection (if provided)
        // IMPORTANT: Partner is for the CURRENT PARENT, not the child!
        if (partnerSelection) {
          if (partnerSelection.type === 'existing' && partnerSelection.partnerId) {
            // Check if this partner is the same as otherParentId (auto-detected spouse)
            // If so, skip - we already handled this above in PATH 1
            const isAlreadyLinked = addPersonContext.otherParentId === partnerSelection.partnerId;

            if (!isAlreadyLinked) {
              // PATH 2: Existing selected other parent (different from auto-detected spouse)
              toast.info(t('tree.toasts.linkingPartner'));

              // Ensure implied couple connection exists (spouse + couple record)
              // This uses the unified helper with minimal metadata
              await ensureImpliedCoupleConnection(
                canvasData.tree.id,
                addPersonContext.sourcePersonId, // current parent
                partnerSelection.partnerId       // selected existing partner
              );

              // Create parent_child relationship between existing partner and child
              try {
                await createRelationship(
                  canvasData.tree.id,
                  partnerSelection.partnerId, // existing partner (person_a)
                  newPersonId, // child (person_b)
                  'parent_child'
                );
              } catch (error: any) {
                // Defensive: If duplicate relationship error (23505), treat as non-fatal
                const isDuplicateError = error?.code === '23505' || error?.message?.includes('unique_relationship');
                if (!isDuplicateError) {
                  throw error; // Re-throw non-duplicate errors
                }
                console.warn('[TreeDashboardPage] Duplicate partner parent_child relationship detected (non-fatal):', error);
              }
            }
          } else if (partnerSelection.type === 'new') {
            // PATH 3: New other parent (create placeholder person + spouse + couple)
            toast.info(t('tree.toasts.creatingPartner'));

            // Get the current parent's name for the partner placeholder
            const currentParent = canvasData.persons.find(p => p.id === addPersonContext.sourcePersonId);
            const currentParentName = currentParent
              ? `${currentParent.first_name} ${currentParent.last_name || ''}`.trim()
              : 'parent';

            // Determine partner gender (opposite of current parent, or 'other')
            const partnerGender: 'male' | 'female' | 'other' =
              currentParent?.gender === 'male' ? 'female' :
              currentParent?.gender === 'female' ? 'male' :
              'other';

            const partnerData = {
              first_name: 'Partner',
              last_name: currentParent?.last_name || '',
              nickname: `Partner of ${currentParentName}`,
              gender: partnerGender,
              date_of_birth: null,
              date_of_death: null,
              marital_status: 'married' as const,
              birth_place: null,
              death_place: null,
              notes: null,
              profile_image_url: null,
            };

            const newPartnerId = await createPerson(canvasData.tree.id, partnerData);

            // Ensure implied couple connection exists (spouse + couple record)
            // This uses the unified helper with minimal metadata
            await ensureImpliedCoupleConnection(
              canvasData.tree.id,
              addPersonContext.sourcePersonId, // current parent
              newPartnerId                     // new partner
            );

            // Create parent_child relationship between new partner and child
            await createRelationship(
              canvasData.tree.id,
              newPartnerId, // new partner (person_a)
              newPersonId, // child (person_b)
              'parent_child'
            );
          }
        }

        toast.success(t('tree.toasts.childAdded', {
          childType: addPersonContext.relationshipType.charAt(0).toUpperCase() + addPersonContext.relationshipType.slice(1)
        }));
      } else if (addPersonContext.type === 'spouse') {
        // Create couple record with metadata (if provided)
        if (coupleMetadata) {
          try {
            await createCouple(
              canvasData.tree.id,
              addPersonContext.sourcePersonId,
              newPersonId,
              coupleMetadata
            );
            console.log('[TreeDashboardPage] Couple record created with metadata');
          } catch (error) {
            console.warn('[TreeDashboardPage] Failed to create couple record (non-fatal):', error);
            // Non-fatal: the relationship already exists in relationships table
            // Couple record is supplementary metadata
          }
        }
        toast.success(t('tree.toasts.spouseAdded'));
      } else if (addPersonContext.type === 'sibling') {
        // SUBMIT-TIME RE-VALIDATION for sibling (double-check with actual person ID)
        const graphContext = buildGraphContext(canvasData);
        const mutation: AddSiblingMutation = {
          type: 'add_sibling',
          sourcePersonId: addPersonContext.sourcePersonId,
          targetPersonId: newPersonId,
          siblingGender: personData.gender === 'male' ? 'male' : 'female',
          parentIds: addPersonContext.parentIds,
        };

        const validationResult = validateMutation(mutation, graphContext);
        if (!validationResult.valid) {
          const firstError = validationResult.violations.find(v => v.severity === 'error');
          throw new Error(firstError?.message || 'Sibling validation failed');
        }

        const siblingType = addPersonContext.relationshipType === 'brother' ? t('tree.relationships.brother') : t('tree.relationships.sister');
        const isHalfSibling = addPersonContext.parentIds?.length === 1;

        toast.success(t('tree.toasts.siblingAdded', {
          siblingType,
          defaultValue: `${siblingType} added successfully${isHalfSibling ? ' (half-sibling)' : ''}`,
        }));
      }

      setShowAddPersonModal(false);
      setAddPersonContext(null);

      // Refresh the tree
      refresh();
    } catch (error) {
      console.error('Failed to add person:', error);
      toast.error(t('tree.errors.addFailed'));
      throw error; // Re-throw to keep modal open
    }
  };

  // Loading state
  if (isLoadingCanvas) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  // NO-TREE STATE: User has completed onboarding but has no tree yet
  // This is a valid state (e.g., after welcome-page skip)
  if (canvasError?.code === 'TREE_NOT_FOUND') {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center max-w-md">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl text-primary">family_history</span>
            </div>
            <h2 className="text-xl font-bold mb-2">{t('dashboard.noTreeFound.title', 'No Tree Yet')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              {t('dashboard.noTreeFound.description', "You haven't created a family tree yet. Ready to begin your journey?")}
            </p>
            <button
              onClick={() => setShowCreateTreeModal(true)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
            >
              {t('dashboard.noTreeFound.createButton', 'Create Your First Tree')}
            </button>
          </div>
        </div>

        {/* Create Tree Modal */}
        <CreateTreeModal
          isOpen={showCreateTreeModal}
          onClose={() => setShowCreateTreeModal(false)}
          onCreateFromProfile={handleCreateTreeFromProfile}
        />
      </>
    );
  }

  // Error state (network errors, access denied, etc.)
  if (canvasError) {
    return <ErrorDisplay error={canvasError} onRetry={refresh} />;
  }

  // Fallback: No tree data but no error (should not happen, but handle gracefully)
  if (!canvasData) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center max-w-md">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl text-primary">family_history</span>
            </div>
            <h2 className="text-xl font-bold mb-2">{t('dashboard.noTreeFound.title', 'No Tree Yet')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              {t('dashboard.noTreeFound.description', "You haven't created a family tree yet. Ready to begin your journey?")}
            </p>
            <button
              onClick={() => setShowCreateTreeModal(true)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
            >
              {t('dashboard.noTreeFound.createButton', 'Create Your First Tree')}
            </button>
          </div>
        </div>

        {/* Create Tree Modal */}
        <CreateTreeModal
          isOpen={showCreateTreeModal}
          onClose={() => setShowCreateTreeModal(false)}
          onCreateFromProfile={handleCreateTreeFromProfile}
        />
      </>
    );
  }

  // Map person detail to component format
  const selectedPerson = personDetail ? {
    id: personDetail.person.id,
    first_name: personDetail.person.first_name,
    last_name: personDetail.person.last_name,
    gender: personDetail.person.gender,
    birth_date: personDetail.person.date_of_birth,
    death_date: personDetail.person.date_of_death,
    profile_image_url: personDetail.person.signedImageUrl || null,
    birth_place: personDetail.person.birth_place,
    death_place: personDetail.person.death_place,
    nickname: personDetail.person.nickname || null,
    notes: personDetail.person.notes || null,
    marital_status: personDetail.person.marital_status || null,
    age: personDetail.person.age,
    lifespan_years: personDetail.person.lifespan_years,
  } : null;

  // TODO: Get real timeline events and linked records from backend
  const timelineEvents: any[] = [];
  const linkedRecords: any[] = [];

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-background dark:bg-background">
      {/* Top Navigation */}
      <TopNavigation
        activeTab="tree"
        persons={canvasData.persons}
        onSelectPerson={handleSearchPersonSelect}
      />

      {/* Main Content Area - iOS Safari fix: prevent parent scroll */}
      <main className="flex-1 flex overflow-hidden relative min-h-0" style={{ touchAction: 'none' }}>
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Left Tool Rail - Hidden on mobile, slide-in with menu */}
        <aside
          className={`
            absolute md:relative inset-y-0 left-0 z-50 transform transition-transform duration-300
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
          `}
        >
          <LeftToolRail
            activeMode={visualizationMode}
            onModeChange={(mode) => {
              handleModeChange(mode);
              setIsMobileMenuOpen(false); // Close menu on selection
            }}
          />
        </aside>

        {/* Mobile Menu Toggle Button - Compact and well-positioned */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="absolute top-3 left-3 z-40 size-10 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg md:hidden flex items-center justify-center border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Toggle menu"
        >
          <span className="material-symbols-outlined text-lg text-slate-700 dark:text-slate-300">
            {isMobileMenuOpen ? 'close' : 'menu'}
          </span>
        </button>

        {/* Center: Tree Canvas */}
        <FamilyTreeCanvas
          persons={canvasData.persons}
          relationships={canvasData.relationships}
          couples={canvasData.couples}
          selectedPersonId={selectedPersonId || undefined}
          treeOwnerId={canvasData.tree.owner_id}
          treeName={canvasData.tree.name}
          totalMembers={canvasData.tree.current_person_count}
          totalGenerations={totalGenerations}
          onPersonSelect={selectPerson}
          canvasTransform={canvasTransform}
          onAddParent={handleAddParent}
          onAddRelative={handleAddRelative}
          onLayoutChange={(positions) => setPersonPositions(positions)}
        />

        {/* Right Details Panel - Responsive behavior */}
        {selectedPersonId && (
          <>
            {/* Mobile: Full-screen overlay */}
            <div
              className={`
                fixed inset-0 bg-white dark:bg-background z-50 transform transition-transform duration-300
                md:hidden
                ${isMobileDetailsPanelOpen ? 'translate-x-0' : 'translate-x-full'}
              `}
            >
              <RightDetailsPanel
                person={selectedPerson}
                timelineEvents={timelineEvents}
                linkedRecords={linkedRecords}
                isLoading={isLoadingDetail}
                onClose={() => {
                  setIsMobileDetailsPanelOpen(false);
                  clearSelection();
                }}
                onPersonUpdated={() => {
                  refresh();
                }}
                onPersonDeleted={() => {
                  setIsMobileDetailsPanelOpen(false);
                  clearSelection();
                  refresh();
                }}
              />
            </div>

            {/* Desktop: Side panel */}
            <div className="hidden md:block">
              <RightDetailsPanel
                person={selectedPerson}
                timelineEvents={timelineEvents}
                linkedRecords={linkedRecords}
                isLoading={isLoadingDetail}
                onClose={clearSelection}
                onPersonUpdated={() => {
                  refresh();
                }}
                onPersonDeleted={() => {
                  clearSelection();
                  refresh();
                }}
              />
            </div>
          </>
        )}

        {/* Mobile Selected Person Quick View (when panel is closed) */}
        {selectedPersonId && !isMobileDetailsPanelOpen && isMobile && (
          <button
            onClick={() => setIsMobileDetailsPanelOpen(true)}
            className="absolute bottom-20 right-3 size-12 bg-primary text-white rounded-full shadow-xl md:hidden animate-in fade-in scale-in duration-200 flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all ring-4 ring-primary/20"
            aria-label="View person details"
          >
            <span className="material-symbols-outlined text-xl">info</span>
          </button>
        )}

        {/* Bottom Control Bar */}
        <BottomControlBar
          zoom={canvasTransform.transform.scale}
          onZoomIn={canvasTransform.zoomIn}
          onZoomOut={canvasTransform.zoomOut}
          onCenter={canvasTransform.centerView}
          onExport={handleExport}
          onPan={handlePan}
          minZoom={0.5}
          maxZoom={2.0}
        />
      </main>

      {/* Add Person Modal */}
      <AddPersonModal
        isOpen={showAddPersonModal}
        onClose={() => {
          setShowAddPersonModal(false);
          setAddPersonContext(null);
        }}
        onSubmit={handleAddPersonSubmit}
        relationshipContext={
          addPersonContext
            ? {
                type: addPersonContext.relationshipType,
                relativeName: addPersonContext.sourcePersonName,
                allowedGenders: addPersonContext.allowedGenders,
              }
            : undefined
        }
        availablePartners={addPersonContext?.availablePartners}
      />

      {/* Relationship Chooser Modal */}
      <RelationshipChooserModal
        isOpen={showRelationshipChooser}
        onClose={() => {
          setShowRelationshipChooser(false);
          setRelationshipChooserPerson(null);
        }}
        onSelectRelationship={handleRelationshipTypeSelected}
        personName={relationshipChooserPerson?.name}
      />
    </div>
  );
}
